import React, { useMemo } from 'react';
import { RCM_SORT_KEY } from './rcmListConstants.js';
import { RCM_HEAD_ROW1_TH, RCM_HEAD_ROW2_TH } from './rcmTableHeaderConstants.js';
import {
  getOrderedVisibleRcmColumnIds,
  isRcmAssertionColumnId,
} from './rcmTableColumnConfig.js';

const ASSERTION_HEADER_LABELS = {
  completeness: 'C',
  existenceOccurrence: 'E&O',
  accuracy: 'A',
  valuation: 'V',
  rightsObligations: 'R&O',
};

function buildHeaderSegments(orderedVisibleIds) {
  const segments = [];
  let i = 0;
  while (i < orderedVisibleIds.length) {
    const id = orderedVisibleIds[i];
    if (isRcmAssertionColumnId(id)) {
      const ids = [];
      while (i < orderedVisibleIds.length && isRcmAssertionColumnId(orderedVisibleIds[i])) {
        ids.push(orderedVisibleIds[i]);
        i += 1;
      }
      segments.push({ type: 'assertions', ids });
      continue;
    }
    segments.push({ type: 'column', id });
    i += 1;
  }
  return segments;
}

function SortableHeaderButton({ label, sortKey, onSortClick, renderSortIcon }) {
  return (
    <button
      type="button"
      onClick={() => onSortClick(sortKey)}
      className="flex items-center gap-0.5 text-[11px] leading-tight text-left font-semibold hover:text-gray-200 rounded px-0.5 -mx-0.5"
    >
      {label}
      {renderSortIcon(sortKey)}
    </button>
  );
}

function renderRow1ColumnHeader(columnId, { onSortClick, renderSortIcon, isLast }) {
  const borderClass = isLast ? '' : 'border-r border-gray-700';
  const thClass = `${RCM_HEAD_ROW1_TH} px-2.5 py-2 text-[11px] leading-tight font-semibold ${
    isLast ? '' : 'border-r border-gray-700'
  }`;

  switch (columnId) {
    case 'srNo':
      return (
        <th
          key={columnId}
          className={`${thClass} text-left whitespace-nowrap`}
          rowSpan={2}
        >
          Sr. No.
        </th>
      );
    case 'classification':
      return (
        <th key={columnId} className={`${thClass} text-left whitespace-nowrap`} rowSpan={2}>
          Classification
        </th>
      );
    case 'soxCycle':
      return (
        <th key={columnId} className={`${thClass} text-left whitespace-nowrap`} rowSpan={2}>
          <SortableHeaderButton
            label="SOX Cycle"
            sortKey={RCM_SORT_KEY.SOX_CYCLE_ID}
            onSortClick={onSortClick}
            renderSortIcon={renderSortIcon}
          />
        </th>
      );
    case 'subProcess':
      return (
        <th
          key={columnId}
          className={`${thClass} text-left whitespace-nowrap`}
          rowSpan={2}
          style={{ minWidth: '120px' }}
        >
          Sub-process
        </th>
      );
    case 'riskId':
      return (
        <th
          key={columnId}
          className={`${thClass} text-left whitespace-nowrap`}
          rowSpan={2}
          style={{ minWidth: '100px' }}
        >
          <SortableHeaderButton
            label="Risk ID"
            sortKey={RCM_SORT_KEY.RISK_ID}
            onSortClick={onSortClick}
            renderSortIcon={renderSortIcon}
          />
        </th>
      );
    case 'clientRiskId':
      return (
        <th
          key={columnId}
          className={`${thClass} text-left whitespace-nowrap`}
          rowSpan={2}
          style={{ minWidth: '110px' }}
        >
          Client Risk ID
        </th>
      );
    case 'riskDescription':
      return (
        <th
          key={columnId}
          className={`${thClass} text-left`}
          rowSpan={2}
          style={{ minWidth: '200px' }}
        >
          Risk Description
        </th>
      );
    case 'controlId':
      return (
        <th
          key={columnId}
          className={`${thClass} text-left whitespace-nowrap`}
          rowSpan={2}
          style={{ minWidth: '100px' }}
        >
          <SortableHeaderButton
            label="Control ID"
            sortKey={RCM_SORT_KEY.CONTROL_ID}
            onSortClick={onSortClick}
            renderSortIcon={renderSortIcon}
          />
        </th>
      );
    case 'controlObjective':
      return (
        <th
          key={columnId}
          className={`${thClass} text-left`}
          rowSpan={2}
          style={{ minWidth: '180px' }}
        >
          Control Objective Description
        </th>
      );
    case 'controlDescription':
      return (
        <th
          key={columnId}
          className={`${thClass} text-left`}
          rowSpan={2}
          style={{ minWidth: '220px' }}
        >
          Control Description
        </th>
      );
    case 'what':
      return (
        <th
          key={columnId}
          className={`${thClass} text-left`}
          rowSpan={2}
          style={{ minWidth: '180px' }}
        >
          <SortableHeaderButton
            label="Control Title"
            sortKey={RCM_SORT_KEY.TITLE}
            onSortClick={onSortClick}
            renderSortIcon={renderSortIcon}
          />
        </th>
      );
    case 'who':
      return (
        <th
          key={columnId}
          className={`${thClass} text-left whitespace-nowrap`}
          rowSpan={2}
          style={{ minWidth: '120px' }}
        >
          Control Owner
        </th>
      );
    case 'keyReports':
      return (
        <th
          key={columnId}
          className={`${thClass} text-left whitespace-nowrap`}
          rowSpan={2}
          style={{ minWidth: '140px' }}
        >
          Key Reports
        </th>
      );
    case 'when':
      return (
        <th
          key={columnId}
          className={`${thClass} text-left whitespace-nowrap`}
          rowSpan={2}
          style={{ minWidth: '100px' }}
        >
          Frequency
        </th>
      );
    case 'controlType':
      return (
        <th key={columnId} className={`${thClass} text-center whitespace-nowrap`} rowSpan={2}>
          Control Type
        </th>
      );
    case 'keyNonKey':
      return (
        <th key={columnId} className={`${thClass} text-center whitespace-nowrap`} rowSpan={2}>
          Key / Non Key
        </th>
      );
    case 'riskCategory':
      return (
        <th
          key={columnId}
          className={`${RCM_HEAD_ROW1_TH} px-2.5 py-2 text-[11px] leading-tight font-semibold text-center whitespace-nowrap ${borderClass}`}
          rowSpan={2}
        >
          Risk Category
        </th>
      );
    default:
      return null;
  }
}

/**
 * Two-row RCM table header (financial assertion sub-columns).
 * Renders in `columnOrder` so gear drag-reorder matches the table.
 */
export function RcmMatrixTableHeader({
  onSortClick,
  renderSortIcon,
  columnVisibility,
  columnOrder,
}) {
  const orderedVisibleIds = useMemo(
    () => getOrderedVisibleRcmColumnIds(columnOrder, columnVisibility),
    [columnOrder, columnVisibility]
  );

  const segments = useMemo(
    () => buildHeaderSegments(orderedVisibleIds),
    [orderedVisibleIds]
  );

  const assertionIdsFlat = useMemo(
    () => segments.filter((s) => s.type === 'assertions').flatMap((s) => s.ids),
    [segments]
  );

  return (
    <thead className="sticky top-0 z-30 shadow-md">
      <tr className="bg-gray-900 text-white">
        {segments.map((segment, segmentIndex) => {
          const isLastSegment = segmentIndex === segments.length - 1;
          if (segment.type === 'assertions') {
            return (
              <th
                key={`assertions-${segment.ids.join('-')}`}
                className={`${RCM_HEAD_ROW1_TH} px-2.5 py-2 text-[11px] leading-tight font-semibold text-center border-b border-gray-700 ${
                  isLastSegment ? '' : 'border-r border-gray-700'
                }`}
                colSpan={segment.ids.length}
              >
                Financial Assertions
              </th>
            );
          }
          return renderRow1ColumnHeader(segment.id, {
            onSortClick,
            renderSortIcon,
            isLast: isLastSegment,
          });
        })}
      </tr>
      {assertionIdsFlat.length > 0 ? (
        <tr className="bg-gray-800 text-white">
          {assertionIdsFlat.map((columnId, index) => (
            <th
              key={columnId}
              className={`${RCM_HEAD_ROW2_TH} px-2 py-1.5 text-[11px] leading-tight font-medium text-center ${
                index === assertionIdsFlat.length - 1 ? '' : 'border-r border-gray-700'
              }`}
              style={{ minWidth: '40px' }}
            >
              {ASSERTION_HEADER_LABELS[columnId]}
            </th>
          ))}
        </tr>
      ) : null}
    </thead>
  );
}
